import React from 'react';

import {
  BaseText,
  Box,
  Icon,
  OnClick,
  TextVariants,
  VisuallyHidden,
} from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton, { ActionButtonSizes, ActionLink } from 'components/action-button';
import { Loyalty } from 'components/app-header/loyalty';
import { truncateTextWithEllipsis } from 'components/app-header/right-nav/utils';
import defaultConfig from 'components/app-header/styled';
import Currency from 'components/currency';
import { Drawer } from 'components/drawer';
import LoadingAnimation from 'components/loading-animation';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { LOYALTY_ERROR_PLACEHOLDER } from 'hooks/loyalty/constants';
import useRoutes from 'hooks/use-routes';
import { useAuthContext } from 'state/auth';
import { usePaymentContext } from 'state/payment';
import { IPaymentMethod } from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { delayUnmountHOC } from 'utils/delay-unmount-hoc';

const PaddedText = styled.span`
  margin-bottom: 3px;
`;

const BottomText = styled.div`
  font-weight: bold;
`;

interface IRightNav {
  onMyAccountClick: OnClick;
  onReFocus: OnClick;
}

const DelayedUnmountDrawer = delayUnmountHOC(Drawer);

const RightNav: React.FC<IRightNav> = ({ onMyAccountClick, onReFocus }) => {
  const { isAuthenticated, user } = useAuthContext();
  const { formatMessage } = useIntl();

  const {
    getBalanceFromPaymentMethods,
    paymentMethods,
    loading: paymentLoading,
    hasGetPaymentMethodsError,
  } = usePaymentContext();
  const { loyaltyDetails, loading: loyaltyLoading } = useLoyaltyActiveCard();
  const { showCardTab, showRewardsTab, hideDrawer, isShowingDrawer } = useUIContext();
  const { getLocalizedRouteForPath } = useRoutes();

  let additionalButtons;

  if (isAuthenticated()) {
    if (paymentLoading || loyaltyLoading) {
      additionalButtons = <LoadingAnimation />;
    } else {
      const prepaidPaymentMethod = paymentMethods.find(
        (method: IPaymentMethod) => (method.prepaid as unknown) as boolean
      );
      let CurrencyWithBalance = () => (
        <Currency amount={getBalanceFromPaymentMethods(prepaidPaymentMethod!)} />
      );

      additionalButtons = (
        <>
          <Box margin="0 1rem 0 0">
            <ActionButton
              size={ActionButtonSizes.SMALL}
              variant={defaultConfig.linkVariant}
              transparent
              onClick={showRewardsTab}
              data-testid="my-rewards-link"
              title={formatMessage({ id: 'myRewards' })}
            >
              <PaddedText>{formatMessage({ id: 'points' }) + '\n'}</PaddedText>
              <BottomText>
                {loyaltyDetails ? loyaltyDetails.points : LOYALTY_ERROR_PLACEHOLDER}
              </BottomText>
            </ActionButton>
          </Box>
          <Box margin="0 1rem 0 0">
            <ActionButton
              size={ActionButtonSizes.SMALL}
              variant={defaultConfig.linkVariant}
              transparent
              onClick={showCardTab}
              data-testid="tim-card-link"
              title={formatMessage({ id: 'timCard' })}
            >
              <PaddedText>{formatMessage({ id: 'timCard' }) + '\n'}</PaddedText>
              {hasGetPaymentMethodsError ? (
                LOYALTY_ERROR_PLACEHOLDER
              ) : (
                <BottomText>
                  <CurrencyWithBalance />
                </BottomText>
              )}
            </ActionButton>
          </Box>
        </>
      );
    }
  }

  if (isAuthenticated()) {
    return (
      <>
        {additionalButtons}
        <DelayedUnmountDrawer
          onDismiss={hideDrawer}
          delayTime={500}
          isVisible={isShowingDrawer}
          disableScroll
        >
          <Loyalty onDismiss={hideDrawer} />
        </DelayedUnmountDrawer>
        <Box margin="0 1rem 0 0">
          <ActionLink
            variant={defaultConfig.linkVariant}
            size={ActionButtonSizes.SMALL}
            transparent
            to={getLocalizedRouteForPath('account')}
            onClick={onMyAccountClick}
            data-testid="my-account-link"
            title={formatMessage({ id: 'myAccount' })}
            onlyIcon
          >
            <Icon icon="profile" color="primary" width="auto" height="1rem" aria-hidden />
            <VisuallyHidden>{formatMessage({ id: 'myAccount' })}</VisuallyHidden>
            <BaseText align="center" color="white" variant={TextVariants.caption}>
              {truncateTextWithEllipsis(user?.details?.name)}
            </BaseText>
          </ActionLink>
        </Box>
      </>
    );
  }

  return (
    <Box margin="0 1rem 0 0">
      <ActionButton
        size={ActionButtonSizes.SMALL}
        variant={defaultConfig.buttonVariant}
        to={getLocalizedRouteForPath('signin')}
        onClick={onReFocus}
        data-testid="desktop-sign-in-link"
      >
        {formatMessage({ id: 'logIn' })}
      </ActionButton>
    </Box>
  );
};

export default RightNav;
